/**
 * Safety incidents: somebody says something happened, and somebody has to look
 * at it.
 *
 * Not feedback. `feedback.ts` is the landing page contact form, answered on a
 * business-day timescale by whoever is on the inbox. This is the queue where
 * being slow is measured in harm, which is why the reporter's own view exists
 * at all: a report that is filed and then invisible is the reason people stop
 * filing them.
 *
 * Three routes, all of them the reporter's own side of the queue. The staff
 * half lives under `/staff/incidents` and is the admin console's, not this
 * app's, so nothing here can read another person's report or move a case along.
 *
 * The types are declared here rather than in `types.ts` because nothing outside
 * this file and the screens that use it has any business with them. They mirror
 * the json tags on `internal/incidents/incidents.go`, field for field, with the
 * investigation trail deliberately left out: the API strips `notes` for a
 * caller who is not staff, so a reporter never receives one and a type that
 * promised it would be lying.
 */

import { api, LIST_ID } from "../api";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import type { EnvelopeMeta } from "../baseQuery";
import type { Meta, Timestamp, UUID } from "../types";

// ── The three enumerations, exactly as the database CHECKs them ──────────────

/**
 * What happened.
 *
 * Written as a const object rather than a bare union so the seven can be
 * iterated for a picker, which is what stops a screen offering six of them.
 */
export const IncidentKinds = {
    Harassment: "harassment",
    UnsafeDriving: "unsafe_driving",
    Accident: "accident",
    VehicleCondition: "vehicle_condition",
    Impersonation: "impersonation",
    Theft: "theft",
    Other: "other",
} as const;
export type IncidentKind = (typeof IncidentKinds)[keyof typeof IncidentKinds];

/**
 * How urgently it needs looking at.
 *
 * The reporter sets this for their own report. The person it happened to is the
 * best judge of how frightening it was, and staff can adjust it afterwards
 * against the rest of the queue.
 */
export const IncidentSeverities = {
    Low: "low",
    Medium: "medium",
    High: "high",
    Critical: "critical",
} as const;
export type IncidentSeverity =
    (typeof IncidentSeverities)[keyof typeof IncidentSeverities];

/** Where the case has got to. */
export const IncidentStatuses = {
    Open: "open",
    Investigating: "investigating",
    Resolved: "resolved",
    Dismissed: "dismissed",
} as const;
export type IncidentStatus =
    (typeof IncidentStatuses)[keyof typeof IncidentStatuses];

/**
 * What the server falls back to when the body carries no severity.
 *
 * Named here so a form can preselect the same value the API would have chosen,
 * rather than the two disagreeing about what "I did not pick one" means.
 */
export const DefaultIncidentSeverity: IncidentSeverity = IncidentSeverities.Medium;

/**
 * The description bound, in runes, matching the CHECK in migration 00024.
 *
 * Runes, not UTF-16 units: the server counts with `utf8.RuneCountInString`, so
 * a description of 3999 emoji is under the limit there and would be 7998 by
 * `String.length` here. Count with `Array.from(text).length`.
 */
export const MaxIncidentDescription = 4000;

// ── The resources ───────────────────────────────────────────────────────────

/** One report. */
export interface Incident {
    id: UUID;

    /**
     * Null once the reporter's account is gone. A report outlives the person
     * who filed it, because an account closing is one of the things that
     * happens after a serious incident.
     */
    reporterId?: UUID;
    reporterName: string;
    reporterEmail?: string;

    /** Absent when the report is not about a particular journey. */
    rideId?: UUID;

    subjectUserId?: UUID;
    subjectUserName?: string;

    kind: IncidentKind;
    severity: IncidentSeverity;

    description: string;
    /** When it happened, if the reporter said. Distinct from `createdAt`. */
    occurredAt?: Timestamp;

    status: IncidentStatus;
    assignedTo?: UUID;

    /**
     * What staff concluded. The database refuses a resolved or dismissed
     * incident without one, so a closed report always has something to show the
     * person who filed it.
     */
    resolution?: string;
    resolvedBy?: UUID;
    resolvedAt?: Timestamp;

    createdAt: Timestamp;
    updatedAt: Timestamp;
}

/**
 * Filing a report.
 *
 * `rideId` and `subjectUserId` are both optional and independently so: a report
 * can be about a ride with no particular person, about a person with no ride,
 * or about neither. The API refuses a report whose subject is the reporter.
 */
export interface ReportIncidentRequest {
    rideId?: UUID;
    subjectUserId?: UUID;
    kind: IncidentKind;
    /** Omitted means medium, chosen by the server. */
    severity?: IncidentSeverity;
    description: string;
    /** RFC3339. The API refuses anything more than an hour into the future. */
    occurredAt?: Timestamp;
}

/** A page of the reporter's own reports, plus the envelope's paging metadata. */
export interface IncidentPage {
    items: Incident[];
    meta?: Meta;
}

export interface MyIncidentsArgs {
    page?: number;
    /** Clamped server-side. */
    limit?: number;
}

/**
 * The per-field reasons behind a 422.
 *
 * `utils.FromError` puts them in the envelope's `data` as `{ fields: {…} }`,
 * keyed by the request field's json name, so `description` and `occurredAt`
 * arrive spelled exactly as this file's request type spells them. Without this
 * a rejected report can only show the envelope's `message`, which for a
 * validation failure is the log line ("validation failed: description: tell us
 * what happened") and belongs in front of nobody.
 *
 * Anything that is not a 422 with fields answers with an empty object, so a
 * caller can read it unconditionally.
 */
export function fieldErrors(error: unknown): Record<string, string> {
    if (!error || typeof error !== "object") return {};

    const candidate = error as FetchBaseQueryError;
    if (!("status" in candidate)) return {};

    const body = candidate.data as { data?: { fields?: unknown } } | undefined;
    const fields = body?.data?.fields;
    if (!fields || typeof fields !== "object") return {};

    const found: Record<string, string> = {};
    for (const [key, value] of Object.entries(fields)) {
        if (typeof value === "string") found[key] = value;
    }
    return found;
}

export const incidentsApi = api.injectEndpoints({
    endpoints: (build) => ({
        /**
         * Files a report. 201 with the created incident.
         *
         * Invalidates the collection, not a single id: the report did not exist
         * a moment ago, so there is no entry to refresh, and what has to change
         * on screen is the reporter's list.
         */
        reportIncident: build.mutation<Incident, ReportIncidentRequest>({
            query: (body) => ({ url: "incidents", method: "POST", body }),
            invalidatesTags: [{ type: "Incident", id: LIST_ID }],
        }),

        /** The caller's own reports, newest first, paginated. */
        myIncidents: build.query<IncidentPage, MyIncidentsArgs | void>({
            query: (args) => ({ url: "incidents/mine", params: args ?? {} }),
            transformResponse: (rows: Incident[] | null, meta: EnvelopeMeta) => ({
                // `rows ?? []` because an empty page is `data: null` on the
                // wire, and a screen that renders `data.length` on null is a
                // crash where an empty state belongs.
                items: rows ?? [],
                meta: meta?.envelope?.meta,
            }),
            providesTags: (result) => [
                { type: "Incident" as const, id: LIST_ID },
                ...(result?.items ?? []).map((incident) => ({
                    type: "Incident" as const,
                    id: incident.id,
                })),
            ],
        }),

        /**
         * One report, for the person who filed it.
         *
         * The API answers 404 for an id that does not exist and 403 for one
         * that belongs to somebody else, and it looks the record up before it
         * checks authorisation so a stranger's id cannot be distinguished from
         * a fictional one.
         */
        incident: build.query<Incident, UUID>({
            query: (incidentId) => `incidents/${incidentId}`,
            providesTags: (_r, _e, incidentId) => [
                { type: "Incident", id: incidentId },
            ],
        }),
    }),
});

export const {
    useReportIncidentMutation,
    useMyIncidentsQuery,
    useIncidentQuery,
} = incidentsApi;
